import { gql } from '@apollo/client';
import { RouteChildrenProps } from 'react-router-dom';

type AllCategories$CategoryProvider = {
  id: string;
  slug: string;
  firstName: string;
  lastName: string;
  publicName: string;
  avatarUrl: string;
  companyName: string;
  rating: number;
  feedbacksCount: number;
  shortDescription: string;
  cityName: string;
  dateRegistered: string;
  featured: string;
  yearsOfExperience: number;
  yearEstablished: number;
};

type AllCategories$CategoryRequest = {
  id: string;
};

export type AllCategories$City = {
  id: string;
  slug: string;
  name: string;
  nameLocativeCase: string;
  lat: number;
  long: number;
  region: {
    id: string;
    name: string;
  };
};

export type AllCategories$Category = {
  slug: string;
  name: string;
};

export type AllCategories$l4Group = {
  l2Id: string;
  name: string;
  l4Categories: AllCategories$Category[];
};

export type AllCategories$TopCategory = {
  id: string;
  slug: string;
  name: string;
  l4Groups: AllCategories$l4Group[];
  feedbacksInfo: {
    averageValue: number;
  };
  providers: AllCategories$CategoryProvider[];
  requests: AllCategories$CategoryRequest[];
};

export type AllCategoriesCity$Props = RouteChildrenProps<{ city: string }> & {
  allCategories: AllCategories$TopCategory[];
  city: AllCategories$City;
  providersCounts: {
    registered: number;
    online: number;
  };
};

export type AllCategoriesNav$Props = {
  topCategories: AllCategories$TopCategory[];
};

export const combineProviders = (categories: AllCategories$TopCategory[], limit?: number) => {
  /* @ts-expect-error type mismatch */
  const providers = categories.reduce((acc, category) => [...acc, ...category.providers], []).filter(Boolean);
  return [...new Set(providers)].slice(0, limit || categories.length);
};

export const combineRequests = (categories: AllCategories$TopCategory[], limit?: number) => {
  /* @ts-expect-error type mismatch */
  const requests = categories.reduce((acc, category) => [...acc, ...category.requests], []).filter(Boolean);
  return [...new Set(requests)].slice(0, limit || categories.length);
};

export const getAverageRating = (categories: AllCategories$TopCategory[]): number => {
  const ratingSum = categories.reduce((acc, cat) => acc + cat.feedbacksInfo.averageValue, 0);
  // Feedback info average value eq 0 means no feedbacks in category,
  // hence we must skip it from calculation.
  const ratingCount = categories.filter((cat) => cat.feedbacksInfo.averageValue > 0).length;
  return ratingSum / ratingCount;
};

export type AllCategoriesResponse = {
  topCategoryList: AllCategories$TopCategory[];
  homepage?: {
    providersCounts?: {
      registered: number;
      online: number;
    };
  };
};

export const allCategoriesQuery = gql`
  query AllCategories($city: String) {
    homepage {
      providersCounts {
        registered
        online
      }
    }
    topCategoryList(city: $city, showHiddenCategories: false) {
      id
      slug
      name
      cityId
      l4Groups(flatten: true) {
        l2Id
        name
        l4Categories {
          id
          slug
          name
        }
      }
      feedbacksInfo {
        averageValue
      }
      requests(limit: 3) {
        id
        user {
          id
          name
          gender
          avatar
        }
        providers {
          id
          avatar
        }
        details {
          question_id
          question
          answer_id
          answer
        }
      }
      providers(limit: 3) {
        id
        slug
        firstName
        lastName
        publicName
        avatarUrl
        companyName
        rating
        feedbacksCount
        shortDescription
        cityName
        dateRegistered
        featured
        yearsOfExperience
        yearEstablished
      }
    }
  }
`;

export type AllCategoriesCityResponse = {
  cityBySlug: {
    id: string;
    slug: string;
    name: string;
    nameLocativeCase: string;
    lat: number;
    long: number;
    region?: {
      id: string;
      name: string;
    };
  };
};

export const allCategoriesCityQuery = gql`
  query AllCategoriesCity($slug: String!) {
    cityBySlug(slug: $slug) {
      id
      slug
      name
      nameLocativeCase
      lat
      long
      region {
        id
        name
      }
    }
  }
`;
